import { MFASecret, MFAVerification, MFAStatus } from '../../types/auth/mfa';

interface APIResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
}

class MFAClient {
  private readonly baseURL: string;

  constructor() {
    this.baseURL = process.env.VITE_API_BASE_URL || 'http://localhost:3001/api';
  }

  /**
   * Generate MFA secret for a user
   */
  async generateSecret(userId: string, accountName: string): Promise<MFASecret> {
    try {
      const response = await fetch(`${this.baseURL}/mfa/generate-secret`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, accountName }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: APIResponse<MFASecret> = await response.json();
      
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to generate MFA secret');
      }

      return result.data;
    } catch (error) {
      console.error('MFA Client: Failed to generate secret:', error);
      throw new Error('Failed to generate MFA secret');
    }
  }

  /**
   * Verify TOTP token or backup code
   */
  async verifyToken(userId: string, token: string): Promise<MFAVerification> {
    try {
      const response = await fetch(`${this.baseURL}/mfa/verify-token`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId, token }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: APIResponse<MFAVerification> = await response.json();
      
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to verify token');
      }

      return result.data;
    } catch (error) {
      console.error('MFA Client: Failed to verify token:', error);
      throw new Error('Failed to verify token');
    }
  }

  /**
   * Get MFA status for a user
   */
  async getStatus(userId: string): Promise<MFAStatus> {
    try {
      const response = await fetch(`${this.baseURL}/mfa/status/${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: APIResponse<MFAStatus> = await response.json();
      
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to get MFA status');
      }

      return result.data;
    } catch (error) {
      console.error('MFA Client: Failed to get status:', error);
      throw new Error('Failed to get MFA status');
    }
  }

  /**
   * Regenerate backup codes
   */
  async regenerateBackupCodes(userId: string): Promise<string[]> {
    try {
      const response = await fetch(`${this.baseURL}/mfa/regenerate-backup-codes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: APIResponse<string[]> = await response.json();
      
      if (!result.success || !result.data) {
        throw new Error(result.error || 'Failed to regenerate backup codes');
      }

      return result.data;
    } catch (error) {
      console.error('MFA Client: Failed to regenerate backup codes:', error);
      throw new Error('Failed to regenerate backup codes');
    }
  }

  /**
   * Disable MFA for a user
   */
  async disableMFA(userId: string): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseURL}/mfa/disable`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result: APIResponse<boolean> = await response.json();
      
      if (!result.success || result.data === undefined) {
        throw new Error(result.error || 'Failed to disable MFA');
      }

      return result.data;
    } catch (error) {
      console.error('MFA Client: Failed to disable MFA:', error);
      throw new Error('Failed to disable MFA');
    }
  }
}

// Export singleton instance
export const mfaClient = new MFAClient();
export default mfaClient;